import React, { useMemo } from "react";
import { Badge, type BadgeProps, Box, Icon, Text } from "@salestrak/ui";
import { AGE_STATUS } from "#app/lib/age-status";
import type {
  ActivityStageInfo,
  ActivityWorkflowInfo,
} from "./activity-types";
import { NoteThread } from "./note-thread";
import type { Omittable } from "@salestrak/ui";

type StageChange = Omittable<{
  oldStage?: ActivityStageInfo | null;
  newStage?: ActivityStageInfo | null;
  oldWorkflow?: ActivityWorkflowInfo | null;
  newWorkflow?: ActivityWorkflowInfo | null;
}>;

type AgeItemProps = Omittable<{
  issueId: string;
  noteId: string;
  ageChangeset: { before?: any; after?: any };
  reason?: { name?: string } | null;
  /** Stage move recorded in the same visit (rendered as a chip). */
  stageChange?: StageChange | null;
  /** The visit (re)assigned the current user as primary. */
  assignedToSelf?: boolean;
}>;

export function AgeItem({
  issueId,
  noteId,
  ageChangeset,
  reason,
  stageChange,
  assignedToSelf,
}: AgeItemProps) {
  const statusBadge = useMemo(() => {
    const status = ageChangeset?.after?.status;
    if (!status) return null;

    const config: Record<
      string,
      { label: string; variant: BadgeProps["variant"] }
    > = {
      [AGE_STATUS.onTrack]: { label: "On Track", variant: "success" },
      [AGE_STATUS.atRisk]: { label: "At Risk", variant: "warning" },
      [AGE_STATUS.offTrack]: { label: "Off Track", variant: "error" },
    };

    return config[status] ?? null;
  }, [ageChangeset]);

  const reasonBadge = useMemo(() => {
    if (reason?.name) return { label: reason.name };
    if (ageChangeset?.after?.reasonId) return { label: "Reason deleted" };
    return null;
  }, [reason, ageChangeset]);

  // Stage move chip — "Old → New" (workflow-prefixed only when it changed too).
  const stageLabel = useMemo(() => {
    const oldStage = stageChange?.oldStage;
    const newStage = stageChange?.newStage;
    if (!newStage) return null;
    const oldWf = stageChange?.oldWorkflow;
    const newWf = stageChange?.newWorkflow;
    const crossWorkflow = !!(oldWf && newWf && oldWf.workflowId !== newWf.workflowId);
    const newLabel =
      crossWorkflow && newWf ? `${newWf.name} › ${newStage.name}` : newStage.name;
    if (oldStage) {
      const oldLabel =
        crossWorkflow && oldWf ? `${oldWf.name} › ${oldStage.name}` : oldStage.name;
      if (oldLabel !== newLabel) return `${oldLabel} → ${newLabel}`;
    }
    return newLabel;
  }, [stageChange]);

  const hasBadges = statusBadge || reasonBadge || stageLabel || assignedToSelf;
  const badges = hasBadges ? (
    <Box direction="row" gap="xs" wrap>
      {statusBadge && (
        <Badge variant={statusBadge.variant}>
          <Icon name="clock" />
          {statusBadge.label}
        </Badge>
      )}
      {reasonBadge && <Badge variant="outline">{reasonBadge.label}</Badge>}
      {stageLabel && (
        <Badge variant="info">
          <Icon name="git-branch" />
          {stageLabel}
        </Badge>
      )}
      {assignedToSelf && (
        <Badge variant="success">
          <Icon name="user" />
          Assigned to self
        </Badge>
      )}
    </Box>
  ) : undefined;

  return <NoteThread issueId={issueId} noteId={noteId} headerExtra={badges} />;
}
